import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Clock,
  CheckCircle,
  Scale,
  Handshake,
  DollarSign,
  MessageCircle,
  Mail,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { useState } from "react";

const FAQSection = () => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How can I pay for my vehicle wash booking?",
      answer: "You can pay for your vehicle wash booking using cash, credit card, debit card, or through our online payment gateway including UPI, digital wallets, and net banking.",
    },
    {
      question: "Is the cost of the vehicle wash covered by insurance?",
      answer: "Vehicle wash services are typically not covered by insurance as they are considered maintenance services. Please check with your insurance provider for specific coverage details.",
    },
    {
      question: "Do I need to book in advance?",
      answer: "While advance booking is recommended to ensure availability, we also accept same-day bookings subject to availability. We recommend booking at least 2-3 hours in advance for better service.",
    },
    {
      question: "What are your service hours?",
      answer: "Our service hours vary by location. Most of our partner wash centers operate from 8 AM to 8 PM, Monday to Sunday. You can check specific timings when booking through our platform.",
    },
    {
      question: "What can I expect during my vehicle wash service?",
      answer: "During your vehicle wash service, our trained professionals will provide a comprehensive cleaning including exterior wash, interior cleaning, and detailing based on the package you've selected.",
    },
    {
      question: "Can I cancel or reschedule my booking?",
      answer: "Yes, you can cancel or reschedule your booking up to 2 hours before your scheduled appointment time. Please refer to our cancellation policy for more details.",
    },
    {
      question: "What if I'm not satisfied with the service?",
      answer: "We strive for customer satisfaction. If you're not satisfied with the service, please contact our support team within 24 hours of service completion, and we'll work to resolve the issue.",
    },
    {
      question: "Do you provide mobile wash services?",
      answer: "Yes, we offer mobile wash services where our professionals come to your location. This service is available in select areas and may have additional charges.",
    },
    {
      question: "What types of vehicles do you service?",
      answer: "We service all types of vehicles including cars, motorcycles, SUVs, and commercial vehicles. Some specialized services may be available for specific vehicle types.",
    },
    {
      question: "How do I track my booking status?",
      answer: "You can track your booking status through our mobile app or website. You'll also receive real-time updates via WhatsApp and SMS notifications.",
    }
  ];

  const toggleFAQ = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section className="bg-white rounded-xl p-8 shadow-sm animate-fade-in">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
        <HelpCircle className="text-blue-600 mr-2" />
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
          {faqs.map((faq, index) => (
          <Card key={index} className="border border-gray-200 hover:border-blue-300 transition-colors">
              <button
              className="w-full p-6 text-left"
                onClick={() => toggleFAQ(index)}
              >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                    {expandedIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                )}
              </div>
              </button>
              {expandedIndex === index && (
              <div className="px-6 pb-6">
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            )}
          </Card>
        ))}
      </div>
    </section>
  );
};

const Terms = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header */}
        <header className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl mb-6 shadow-xl">
              <Scale className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Terms of Service
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our terms of service and frequently asked questions about our vehicle wash booking platform.
            </p>
            <Badge variant="secondary" className="mt-4 bg-blue-100 text-blue-800 border-blue-200 px-4 py-2 text-sm font-medium">
              <Clock className="w-4 h-4 mr-2" />
              Effective Date: {formattedDate}
            </Badge>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-12 space-y-8">
          {/* Service Overview */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900 flex items-center">
                <Handshake className="w-8 h-8 text-blue-600 mr-3" />
                Service Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                Timelyyy is a vehicle wash booking platform that connects customers with professional vehicle wash services. Our platform allows you to book, schedule, and pay for vehicle wash services through our website and mobile application.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                By using our service, you agree to these terms and conditions. Please read them carefully before using our platform.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to modify these terms at any time. Continued use of our service after changes constitutes acceptance of the new terms.
              </p>
            </CardContent>
          </Card>

          {/* Booking Terms */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                Booking Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                When booking a vehicle wash service through our platform:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>All bookings are subject to availability</li>
                <li>You must provide accurate vehicle and contact information</li>
                <li>Payment is required at the time of booking</li>
                <li>Cancellations must be made at least 2 hours in advance</li>
                <li>We are not responsible for any damage to personal items left in the vehicle</li>
                <li>Service quality may vary by location and service provider</li>
              </ul>
            </CardContent>
          </Card>

          {/* Payment Terms */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <DollarSign className="w-6 h-6 text-green-600 mr-3" />
                Payment Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                Payment for services must be made in advance through our secure payment gateway. We accept:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                <li>Credit and debit cards</li>
                <li>UPI payments</li>
                <li>Digital wallets (Google Pay, PhonePe, Paytm)</li>
                <li>Net banking</li>
                <li>Cash on service (select locations only)</li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                All prices are inclusive of applicable taxes. Refunds are subject to our refund policy.
              </p>
            </CardContent>
          </Card>

          {/* User Responsibilities */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                User Responsibilities
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a user of our platform, you agree to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Provide accurate and complete information</li>
                <li>Use the service only for lawful purposes</li>
                <li>Not interfere with the operation of the platform</li>
                <li>Respect the rights of other users and service providers</li>
                <li>Report any issues or concerns promptly</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <FAQSection />

          {/* Contact Information */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Mail className="w-6 h-6 text-blue-600 mr-3" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have any questions about our terms of service or need assistance, please contact us:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Email Support</p>
                    <p className="text-sm text-gray-600">support@timelyyy.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">WhatsApp Support</p>
                    <p className="text-sm text-gray-600">+91 98765 43210</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>

      </div>
  );
};

export default Terms;